import { Component, ElementRef, Inject, Optional, ViewChild } from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { RETURN_URL_QUERY_PARAM } from '../../utils/return-url.util';
import {
  LEARN_MORE,
  LEARN_MORE_TOPICS,
  LearnMoreContent,
  LearnMoreJourney,
  LearnMoreTopic,
  MIN_STAT_TO_SHOW,
  PublicStats,
} from './learn-more.content';

/** What LearnMoreService passes in - the stats and URL syncing come from it, so this file doesn't import it. */
export interface LearnMoreDialogData {
  topic: LearnMoreTopic;
  stats$: Observable<PublicStats | null>;
  topicChanged: (topic: LearnMoreTopic) => void;
}

/** How far down the sheet has to be dragged to close it. */
const SWIPE_CLOSE_PX = 120;

// The home page's "Learn more" for each service. A bottom sheet on a phone, a dialog elsewhere (LearnMoreService).
@Component({
  templateUrl: './learn-more-dialog.component.html',
  styleUrls: ['./learn-more-dialog.component.css'],
})
export class LearnMoreDialogComponent {
  static readonly TITLE_ID = 'learn-more-title';
  readonly titleId = LearnMoreDialogComponent.TITLE_ID;
  readonly minStat = MIN_STAT_TO_SHOW;

  @ViewChild('body') body?: ElementRef<HTMLElement>;

  readonly data: LearnMoreDialogData;
  readonly isSheet: boolean;
  topic!: LearnMoreTopic;
  content!: LearnMoreContent;
  // A one-item list, swapped for a new one on each pick, so *ngFor re-renders the steps and they animate in again
  shownJourney: LearnMoreJourney[] = [];

  private dragStartY: number | null = null;

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) dialogData: LearnMoreDialogData | null,
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) sheetData: LearnMoreDialogData | null,
    @Optional() private dialogRef: MatDialogRef<LearnMoreDialogComponent> | null,
    @Optional() private sheetRef: MatBottomSheetRef<LearnMoreDialogComponent> | null,
    private host: ElementRef<HTMLElement>,
  ) {
    this.data = (sheetData ?? dialogData) as LearnMoreDialogData;
    this.isSheet = !!sheetRef;
    this.show(this.data.topic);
  }

  get nextTopic(): LearnMoreTopic {
    return LEARN_MORE_TOPICS[(LEARN_MORE_TOPICS.indexOf(this.topic) + 1) % LEARN_MORE_TOPICS.length];
  }

  get next(): LearnMoreContent {
    return LEARN_MORE[this.nextTopic];
  }

  /** Sign-up, then straight to the part of the app this was about. */
  get signupQueryParams(): Record<string, string> {
    return this.content.cta.afterSignup ? { [RETURN_URL_QUERY_PARAM]: this.content.cta.afterSignup } : {};
  }

  statCount(stats: PublicStats | null): number | null {
    const count = this.content.stat && stats ? stats[this.content.stat.key] : null;
    return typeof count === 'number' && count >= MIN_STAT_TO_SHOW ? count : null;
  }

  showJourney(journey: LearnMoreJourney): void {
    if (journey !== this.shownJourney[0]) {
      this.shownJourney = [journey];
    }
  }

  /** "Next: Buy & sell" - the next service, in place, without closing. */
  showNext(): void {
    this.show(this.nextTopic);
    this.data.topicChanged(this.topic);
    this.body?.nativeElement.scrollTo({ top: 0 });
  }

  close(): void {
    this.dialogRef?.close();
    this.sheetRef?.dismiss();
  }

  // Swipe the sheet down by its header to close it (dialogs don't drag)
  dragStart(event: PointerEvent): void {
    if (!this.isSheet || (event.target as HTMLElement).closest('button')) {
      return;
    }
    this.dragStartY = event.clientY;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    this.sheetContainer()?.classList.add('learn-more-dragging');
  }

  dragMove(event: PointerEvent): void {
    if (this.dragStartY === null) {
      return;
    }
    const dy = Math.max(0, event.clientY - this.dragStartY);
    const container = this.sheetContainer();
    if (container) {
      container.style.transform = `translateY(${dy}px)`;
    }
  }

  dragEnd(event: PointerEvent): void {
    if (this.dragStartY === null) {
      return;
    }
    const dy = event.clientY - this.dragStartY;
    this.dragStartY = null;
    const container = this.sheetContainer();
    container?.classList.remove('learn-more-dragging');
    if (dy > SWIPE_CLOSE_PX) {
      this.close();
    } else if (container) {
      container.style.transform = '';
    }
  }

  private show(topic: LearnMoreTopic): void {
    this.topic = topic;
    this.content = LEARN_MORE[topic];
    this.shownJourney = this.content.journeys?.length ? [this.content.journeys[0]] : [];
  }

  private sheetContainer(): HTMLElement | null {
    return this.host.nativeElement.closest('mat-bottom-sheet-container');
  }
}
